import {
  Avatar,
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  orderDetailsContainerStyle,
  subContainerHeadingStyle,
  subContainerStyle,
  additionalInfoTextStyle,
  digitalProductTagStyle,
} from "../styles/styles";
import DigitalDeliveryNotice from "./DigitalDeliveryNotice";
import CustomTooltip from "../../../Components/CustomTooltip";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { formatPrice } from "../../../Helpers/formating";
import { getCurrencySymbol } from "../../../Utils/CountriesData/getCountriesData";

const headerCellStyle = {
  fontSize: "13px",
  fontWeight: 500,
  color: "rgba(62, 63, 76, 0.6)",
  borderBottom: "1px solid rgba(212, 212, 212, 0.5)",
  padding: "10px 12px",
};
const bodyCellStyle = {
  fontSize: "14px",
  color: "rgba(62, 63, 76, 0.9)",
  borderBottom: "1px solid rgba(212, 212, 212, 0.5)",
  padding: "8px 12px",
};
const stepperButtonStyle = {
  padding: "2px",
  color: "rgba(62, 63, 76, 0.7)",
  border: "1px solid rgba(212, 212, 212, 0.9)",
  borderRadius: "6px",
  "&.Mui-disabled": { borderColor: "rgba(212, 212, 212, 0.4)" },
};

/**
 * Product Job page — left panel. Shows the "Ordered Products" table (image,
 * name, Qty, Product ID, Private SKU, Price, and a Requests tooltip carrying the
 * member's per-item note) plus the free-text order notes. COMY1-283.
 */
const ProductOrderDetails = (props) => {
  const {
    jobData,
    viewType,
    // Re-approval flow (pending product orders): supplier can un-check items.
    showAvailability = false,
    unavailableIds = [],
    onToggleItem,
    // …and cut a line down to what they actually have (2 ordered, 1 in stock).
    // Keyed by item id; an item missing from the map is unchanged.
    quantities = {},
    onQuantityChange,
  } = props;
  const { ordered_products = [], additional_information, order_summary } = jobData;
  const { t } = useTranslation("page-my-jobs", { keyPrefix: "table_data" });
  const { t: tModal } = useTranslation("view-job-modal");
  const { t: tHatavot } = useTranslation("hatavot");
  const tokens = useTheme().palette.customColors;
  const currencySymbol = getCurrencySymbol(order_summary?.currency);
  const description = additional_information?.description?.trim() || null;
  // Digital items are sent to the client electronically — the notice under the
  // table tells the supplier to do that (COMY1-334). Un-checked (unavailable)
  // items don't count: the supplier isn't sending those at all.
  const hasDigitalProducts = ordered_products.some(
    (item) =>
      item.is_digital &&
      item.is_available !== false &&
      !unavailableIds.includes(item.id),
  );

  // Read-only views (active/completed/missed): push the crossed-out
  // (unavailable) items to the bottom so the fulfilled ones read first. During
  // the interactive un-check flow keep the original order so rows don't jump
  // around as the supplier clicks.
  const rows = showAvailability
    ? ordered_products
    : [...ordered_products].sort(
        (a, b) =>
          Number(a.is_available === false) - Number(b.is_available === false),
      );

  // What the supplier says they can supply on this line. An un-checked item is
  // none of it, so the checkbox and the stepper can never disagree.
  const quantityOf = (item) =>
    unavailableIds.includes(item.id)
      ? 0
      : quantities[item.id] ?? item.quantity;

  // Once anything is cut, show what the member will be asked to approve —
  // otherwise the supplier is changing an order without seeing its new total.
  const hasChanges =
    showAvailability &&
    ordered_products.some((item) => quantityOf(item) !== item.quantity);
  const revisedTotal = ordered_products.reduce(
    (sum, item) => sum + (item.unit_price ?? 0) * quantityOf(item),
    0,
  );

  return (
    <Box className="hide-scrollbar" sx={orderDetailsContainerStyle}>
      <Box sx={subContainerStyle}>
        <Typography sx={subContainerHeadingStyle}>
          {t("ordered_products")}
        </Typography>

        <Box
          sx={{
            border: "1px solid rgba(212, 212, 212, 0.5)",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Table
            size="small"
            sx={{
              // Vertical dividers between columns (match the Figma) — logical
              // start-edge border so it works in both RTL and LTR.
              "& td:not(:first-of-type), & th:not(:first-of-type)": {
                borderInlineStart: "1px solid rgba(212, 212, 212, 0.5)",
              },
            }}
          >
            <TableHead>
              <TableRow>
                {showAvailability && (
                  <TableCell sx={{ ...headerCellStyle, width: 44 }} />
                )}
                <TableCell sx={headerCellStyle}>{t("product_name")}</TableCell>
                <TableCell sx={headerCellStyle}>{t("qty")}</TableCell>
                <TableCell sx={headerCellStyle}>{t("product_id")}</TableCell>
                <TableCell sx={headerCellStyle}>{t("private_sku")}</TableCell>
                <TableCell sx={headerCellStyle}>{t("price")}</TableCell>
                <TableCell sx={headerCellStyle} align="center">
                  {t("requests")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((item) => {
                // Availability is driven by the live checkbox state; the parent
                // seeds unavailableIds from the backend's is_available so a
                // returned order opens with the right boxes already un-checked.
                const isUnavailable = unavailableIds.includes(item.id);
                // Fade + strike-through the whole entry when un-checked, to
                // match how the member sees an unavailable item (opacity-50 +
                // line-through). The image isn't text so it just fades.
                const rowSx = isUnavailable
                  ? { "& td": { opacity: 0.5, textDecoration: "line-through" } }
                  : null;
                return (
                <TableRow
                  key={item.id}
                  onClick={
                    showAvailability
                      ? () => onToggleItem?.(item.id)
                      : undefined
                  }
                  sx={{
                    ...rowSx,
                    ...(showAvailability
                      ? {
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                        }
                      : {}),
                  }}
                >
                  {showAvailability && (
                    <TableCell sx={{ ...bodyCellStyle, width: 44 }}>
                      {/* Display-only — the whole row is the click target. */}
                      <Checkbox
                        checked={!isUnavailable}
                        readOnly
                        tabIndex={-1}
                        icon={
                          <img
                            src="/icons/checked-icons/checkbox-uncheck-icon.svg"
                            alt=""
                            width={18}
                            height={18}
                          />
                        }
                        checkedIcon={
                          <img
                            src="/icons/checked-icons/checkbox-checked-lightGreen-icon.svg"
                            alt=""
                            width={18}
                            height={18}
                          />
                        }
                        sx={{ p: 0, pointerEvents: "none" }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={bodyCellStyle}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Avatar
                        src={item.image_url || ""}
                        alt={item.name}
                        variant="rounded"
                        sx={{ width: 32, height: 32, bgcolor: "rgba(212, 212, 212, 1)" }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: "14px" }}>
                          {item.name}
                          {item.is_digital && (
                            <Box component="span" sx={digitalProductTagStyle}>
                              {tModal("digital_product")}
                            </Box>
                          )}
                        </Typography>
                        {/* Set-deal line (bundle / N-for-M / 1+1): list what the
                            set is made of so the supplier knows what to
                            prepare (COMY1-359). */}
                        {Array.isArray(item.hatava_set_items) &&
                          item.hatava_set_items.length > 0 && (
                            <Box sx={{ paddingInlineStart: "10px", marginTop: "2px" }}>
                              <Typography sx={{ fontSize: "12px", color: tokens.textSoft }}>
                                {tHatavot("job.set_contents")}
                              </Typography>
                              {item.hatava_set_items.map((setItem, idx) => (
                                <Typography
                                  key={setItem.id ?? idx}
                                  sx={{ fontSize: "12px", color: tokens.textSoft }}
                                >
                                  {"• "}
                                  {tHatavot("job.set_item", {
                                    name: setItem.name,
                                    quantity: setItem.quantity ?? 1,
                                  })}
                                </Typography>
                              ))}
                            </Box>
                          )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {showAvailability ? (
                      /* The supplier sets what they can actually supply. One
                         short of the order is a reduction; zero is the same as
                         un-checking the row, so the two controls never
                         disagree. */
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: "2px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconButton
                          size="small"
                          aria-label={tModal("quantity_less")}
                          disabled={quantityOf(item) <= 0}
                          onClick={() =>
                            onQuantityChange?.(item.id, quantityOf(item) - 1)
                          }
                          sx={stepperButtonStyle}
                        >
                          <RemoveIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            minWidth: "34px",
                            textAlign: "center",
                            textDecoration: "none",
                            color: "rgba(62, 63, 76, 0.9)",
                          }}
                        >
                          {quantityOf(item)}
                          {quantityOf(item) !== item.quantity && (
                            <Box
                              component="span"
                              sx={{ color: "rgba(62, 63, 76, 0.45)" }}
                            >
                              /{item.quantity}
                            </Box>
                          )}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={tModal("quantity_more")}
                          disabled={quantityOf(item) >= item.quantity}
                          onClick={() =>
                            onQuantityChange?.(item.id, quantityOf(item) + 1)
                          }
                          sx={stepperButtonStyle}
                        >
                          <AddIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    ) : isUnavailable || quantityOf(item) === item.quantity ? (
                      item.quantity
                    ) : (
                      /* Cut down when the order was returned, and approved that
                         way by the member — what to prepare, out of what was
                         ordered. */
                      <>
                        {quantityOf(item)}
                        <Box
                          component="span"
                          sx={{ color: "rgba(62, 63, 76, 0.45)" }}
                        >
                          /{item.quantity}
                        </Box>
                      </>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellStyle}>{item.product_id}</TableCell>
                  <TableCell sx={bodyCellStyle}>{item.private_sku || "-"}</TableCell>
                  <TableCell sx={bodyCellStyle}>
                    {/* The community discount is per category, so each line
                        carries its own — list price with what the member
                        actually pays beside it. */}
                    {item.unit_price != null &&
                    Number(item.unit_price) !== Number(item.list_price)
                      ? `${currencySymbol}${formatPrice(item.list_price)} (${currencySymbol}${formatPrice(item.unit_price)})`
                      : `${currencySymbol}${formatPrice(item.list_price ?? item.unit_price)}`}
                  </TableCell>
                  <TableCell
                    sx={bodyCellStyle}
                    align="center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <CustomTooltip
                      title={item.client_input || t("no_requests")}
                      tooltipSxStyles={{ paddingY: "5px" }}
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: "18px",
                          color: item.client_input
                            ? "rgba(28, 160, 138, 1)"
                            : "rgba(62, 63, 76, 0.4)",
                          cursor: "pointer",
                        }}
                      />
                    </CustomTooltip>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {hasChanges && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "8px",
              marginTop: "10px",
              padding: "8px 12px",
              borderRadius: "8px",
              backgroundColor: "rgba(255, 244, 229, 1)",
            }}
          >
            <Typography sx={{ fontSize: "13px", color: "rgba(62, 63, 76, 0.9)" }}>
              {tModal("revised_order_note")}
            </Typography>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap" }}>
              {currencySymbol}
              {formatPrice(revisedTotal)}
            </Typography>
          </Box>
        )}
      </Box>

      {description && viewType !== "missed" && (
        <Box sx={subContainerStyle}>
          <Typography sx={subContainerHeadingStyle}>
            {tModal("additional_info")}
          </Typography>
          <Typography sx={additionalInfoTextStyle}>{description}</Typography>
        </Box>
      )}

      {/* Digital-delivery notice sits at the foot of the panel (COMY1-334) —
          `marginTop: auto` pushes it down when the order is short so it reads
          as a closing instruction rather than a note on the items table. */}
      {hasDigitalProducts && (
        <Box sx={{ marginTop: "auto" }}>
          <DigitalDeliveryNotice />
        </Box>
      )}
    </Box>
  );
};

export default ProductOrderDetails;
